import React from 'react';

import { CSSObject } from 'styled-components';

import { isItemSelected } from '../helpers';
import { Menu, MenuItem } from '../layout';
import { MenuProps } from '../layout/Menu/Menu';
import {
  DefaultItem,
  GenericItem,
  GetItemProps,
  GetMenuProps,
  RenderMenuItem,
  TargetProps,
} from '../types';

type MultiSelectMenuProps<TItem extends GenericItem> = MenuProps & {
  menuItems: TItem[];
  getMenuProps: GetMenuProps;
  getItemProps: GetItemProps<TItem>;
  highlightedIndex: number;
  addSelectedItem: (item: TItem) => void;
  removeSelectedItem: (item: TItem) => void;
  targetProps: TargetProps<HTMLElement>;
  renderMenuItem?: RenderMenuItem<TItem>;
  selectedItems?: TItem[];
};

const MultiSelectMenu = <TItem extends GenericItem>({
  menuItems,
  getItemProps,
  renderMenuItem = ({ item, ...itemProps }) => (
    <MenuItem
      {...itemProps}
      text={(item as unknown as DefaultItem).label}
      subtext={(item as unknown as DefaultItem).subtext}
    />
  ),
  selectedItems,
  highlightedIndex,
  addSelectedItem,
  removeSelectedItem,
  children,
  ...props
}: MultiSelectMenuProps<TItem>) => {
  return (
    <Menu {...props}>
      {menuItems.map((item, index) => {
        const isSelected = isItemSelected({ selectedItems, item });
        const { style, ...itemProps } = getItemProps?.({ item, index }) || {};
        return renderMenuItem({
          key: index,
          style: style as CSSObject,
          ...itemProps,
          onClick: () => {
            if (item.disabled) {
              return;
            }
            isSelected ? removeSelectedItem(item) : addSelectedItem(item);
          },
          item,
          isMultiple: true,
          text: (item as unknown as DefaultItem)?.label,
          subtext: (item as unknown as DefaultItem)?.subtext,
          disabled: item.disabled,
          isHighlighted: highlightedIndex === index,
          isSelected,
        });
      })}
      {children}
    </Menu>
  );
};

export default MultiSelectMenu;
